import { createAppearance } from './appearance.js';
import { createTranslator } from './i18n.js';
import { registerBehaviors } from './behaviors.js';
import { registerFormBehaviors } from './forms.js';
import { registerTableBehaviors } from './table.js';
import { copy } from './clipboard.js';
import { announce } from './announce.js';
import { MellowSlider } from './elements/slider.js';
import { MellowOtp } from './elements/otp.js';
import { MellowDropdown, MellowContext } from './elements/dropdown.js';
import { MellowTooltip } from './elements/tooltip.js';
import { MellowModal, modal, closeAllModals, registerModalBehaviors } from './elements/modal.js';
import { MellowToastRegion, toast, registerToastBehaviors } from './elements/toast.js';
import { MellowSelect } from './elements/select.js';
import { MellowSidebar, MellowTablist, registerNavigationBehaviors } from './elements/navigation.js';
import { MellowAutocomplete, MellowCommand, registerShortcuts } from './elements/combobox.js';
import { MellowCalendar } from './elements/calendar.js';
import { MellowDatePicker } from './elements/date-picker.js';
import { MellowChart } from './elements/chart.js';
import { MellowToggle, MellowToggleGroup } from './elements/toggle.js';
import { MellowColorPicker } from './elements/color-picker.js';
import { MellowPhone } from './elements/phone.js';
import { MellowCarousel } from './elements/carousel.js';
import { MellowEditor, registerComposer } from './elements/editor.js';
import { MellowMarkdownEditor } from './elements/markdown-editor.js';
import { MellowCropper, registerCropper } from './elements/cropper.js';
import { MellowStatusHistory } from './elements/status-history.js';

const config = window.MellowConfig ?? {};

// CSP: the nonce of our own <script> (from Vite::useCspNonce()), for <style> elements created at runtime.
const nonce = document.currentScript?.nonce || document.querySelector('script[nonce]')?.nonce || '';

const Mellow = {
    config,
    nonce,
    appearance: null,
    t: createTranslator(config.translations),
    announce,
    modal,
    closeModals: closeAllModals,
    toast,
    /** Mellow.copy(text) → Promise<boolean>; works on plain-HTTP LAN addresses too. */
    copy,

    /** Defines a custom element once, so re-evaluating the bundle is harmless. */
    define(name, constructor) {
        if (!customElements.get(name)) customElements.define(name, constructor);
    },
};

if (config.appearance !== false) {
    Mellow.appearance = createAppearance(config.appearance ?? 'system', config.appearanceSource === 'server');
    // Re-apply after Livewire's wire:navigate swaps the page.
    document.addEventListener('livewire:navigated', () => Mellow.appearance.apply());
}

// Mellow::refreshTheme(): swap the theme variables (and appearance) without a reload.
window.addEventListener('mellow-theme', ({ detail = {} }) => {
    let style = document.head.querySelector('style[data-mellow-theme]');
    if (!style) {
        style = document.createElement('style');
        style.dataset.mellowTheme = '';
        if (nonce) style.nonce = nonce;
        document.head.append(style);
    }
    style.textContent = detail.css ?? '';
    if (detail.appearance && Mellow.appearance && Mellow.appearance.value !== detail.appearance) {
        Mellow.appearance.value = detail.appearance;
    }
});

// Expose the API before defining elements: existing elements upgrade synchronously and may use it.
window.Mellow = Mellow;

registerBehaviors();
registerFormBehaviors(Mellow.t);
registerTableBehaviors(Mellow.t);

Mellow.define('mellow-slider', MellowSlider);
Mellow.define('mellow-otp', MellowOtp);
Mellow.define('mellow-dropdown', MellowDropdown);
Mellow.define('mellow-context', MellowContext);
Mellow.define('mellow-tooltip', MellowTooltip);
Mellow.define('mellow-modal', MellowModal);
Mellow.define('mellow-toast-region', MellowToastRegion);
Mellow.define('mellow-select', MellowSelect);
Mellow.define('mellow-sidebar', MellowSidebar);
Mellow.define('mellow-tablist', MellowTablist);
Mellow.define('mellow-autocomplete', MellowAutocomplete);
Mellow.define('mellow-command', MellowCommand);
Mellow.define('mellow-calendar', MellowCalendar);
Mellow.define('mellow-date-picker', MellowDatePicker);
Mellow.define('mellow-chart', MellowChart);
Mellow.define('mellow-toggle', MellowToggle);
Mellow.define('mellow-toggle-group', MellowToggleGroup);
Mellow.define('mellow-color-picker', MellowColorPicker);
Mellow.define('mellow-phone', MellowPhone);
Mellow.define('mellow-carousel', MellowCarousel);
Mellow.define('mellow-editor', MellowEditor);
Mellow.define('mellow-markdown-editor', MellowMarkdownEditor);
Mellow.define('mellow-cropper', MellowCropper);
Mellow.define('mellow-status-history', MellowStatusHistory);

registerModalBehaviors();
registerToastBehaviors();
registerNavigationBehaviors();
registerShortcuts();
registerComposer();
registerCropper();

export default Mellow;
